import {
  ATTACHMENT_SUPPORTED_MIME_TYPES,
  ATTACHMENT_MAX_SIZE_MB,
} from "../attachments/attachments.types.ts";
import { MAP_IMAGE_URLS, PLACEHOLDER_IMAGE } from "../image/image.ts";
import { SCREEN_JSX_DESCRIPTION } from "../prompts/concepts.ts";

export const UPLOAD_MIME_TYPES = ATTACHMENT_SUPPORTED_MIME_TYPES;
export const TEXT_ATTACHMENT_MAX_LENGTH = 64_000;

export const TEXT_DOCUMENT_MIME_TYPES = [
  "text/markdown",
  "text/plain",
  "text/javascript",
] as const;

/** Image rules for a coding assistant writing JSX. Generation prompts keep their alias wording. */
const jsxImageRules = (): string => `Images in jsx:
- Put an image in img src, or in className as bg-[url(...)]. Give every img an alt: describe the image, or alt="" if it is purely decorative. Size it for its purpose; use w-full h-full object-cover only when the image fills its container.
- Take every image from one of these sources, checked in this order:
  1. An image the user supplied: run the ${MCP_TOOLS.uploadAttachment.name} curl, then paste that assetUrl verbatim. A screen with an assetUrl whose file was never uploaded, is empty, or is over the size limit is rejected and not saved. Use it only when type is "image", where the user asked for it or, on a ui-fidelity screen, where it clearly belongs.
  2. An image already in this file, or in another file you read with ${MCP_TOOLS.getScreenJsx.name} (pass that fileId in imageSourceFileIds): keep its URL and any data-* attributes exactly as ${MCP_TOOLS.getScreenJsx.name} returned them. Never put an image from a ui-fidelity screen on a lofi or hifi screen; use a placeholder (4) there instead.
  3. A map on a ui-fidelity screen: one of ${MAP_IMAGE_URLS.join(", ")}.
  4. Any other image on a lofi or hifi screen, maps included: a placeholder such as ${PLACEHOLDER_IMAGE.src}, where the numbers give the size.
- If a ui-fidelity screen would naturally include a photo (hero, cover, avatar, product) that none of these sources gives, ask the user before calling ${MCP_TOOLS.addScreenJsx.name} or ${MCP_TOOLS.replaceScreenJsx.name}. Offer three options: supply the photo, use ${MCP_TOOLS.aiDesignScreens.name} (which can find stock photos and use images from a webpage), or continue without photos. If they continue without, stand in for the photo with a gradient, pattern, icon, initials, or a simple flat illustration in CSS or inline SVG. Do not try to recreate the photo itself with CSS or SVG, and never write an image URL from memory, images.unsplash.com included. A screen with an Unsplash photo that is not already in this file or in a file in imageSourceFileIds is rejected and not saved.
- Icons, gradients, and shapes are fine for decorative and UI elements.`;

export const MCP_TOOLS = {
  // File tools
  listFiles: {
    name: "list-files",
    title: "List Files",
    get description() {
      return `List metadata for the current user's Flowstep files. Call this first to check if a relevant file already exists. No screen content — use ${MCP_TOOLS.getScreenImage.name} or ${MCP_TOOLS.getScreenJsx.name}.

Returns { files: [{ id, name, created_at, updated_at, organisation_id, shared_with_me, owner, url }] }. Default order is newest created_at first; orderByCreation false is newest updated_at first.

- owner: true if the caller created the file.
- shared_with_me: shared with the caller directly, not only via the organisation.
- url: opens the file in the Flowstep editor.

A full page may mean more files exist — fetch the next page with offset.`;
    },
    shortDescription: "List the files in your account.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  getFile: {
    name: "get-file",
    title: "Get File",
    get description() {
      return `Get metadata for one of the current user's Flowstep files. No screen content — use ${MCP_TOOLS.getScreenImage.name} or ${MCP_TOOLS.getScreenJsx.name}.

Returns { file: { id, name, created_at, updated_at, project_id, access_level, visibility, owner, url }, user_access_level }.

- owner: true if the caller created the file.
- url: opens the file in the Flowstep editor.`;
    },
    shortDescription: "Get a file in your account.",
    pricing: "free",
    annotations: {
      readOnlyHint: false, // auto-accepts pending invites as a side effect
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  createFile: {
    name: "create-file",
    title: "Create File",
    get description() {
      return `Create a blank Flowstep file for the current user, with no screens. Prefer ${MCP_TOOLS.aiDesignScreens.name} without a fileId when you also want screens generated. Use this only for code-first workflows, then ${MCP_TOOLS.addScreenJsx.name}.

Returns { file: { id, name, created_at, updated_at, project_id, access_level, visibility, owner, url }, user_access_level }.

- owner: always true (the caller created the file).
- user_access_level: always "write".
- url: opens the file in the Flowstep editor.`;
    },
    shortDescription: "Create a file in your account.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  updateFile: {
    name: "update-file",
    title: "Update File",
    description: `Rename one of the current user's Flowstep files.

Returns { file: { id, name, created_at, updated_at, project_id, access_level, visibility, owner, url }, user_access_level }.

- owner: true if the caller created the file.
- url: opens the file in the Flowstep editor.`,
    shortDescription: "Rename a file in your account.",
    pricing: "free",
    annotations: {
      readOnlyHint: false, // rename + follow-up MCP_TOOLS.getFile.name auto-accepts pending invites as a side effect
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  deleteFile: {
    name: "delete-file",
    title: "Delete File",
    description: `Permanently delete one of the current user's Flowstep files. Confirm with the user first. name must match the current display name or deletion is aborted.

Returns { deleted: true }.`,
    shortDescription: "Delete a file from your account.",
    pricing: "free",
    annotations: {
      readOnlyHint: false, // name check goes through MCP_TOOLS.getFile.name, which auto-accepts pending invites as a side effect
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: false,
    },
  },

  // Screen tools
  listScreens: {
    name: "list-screens",
    title: "List Screens",
    get description() {
      return `List metadata for screens in one of the current user's Flowstep files. Call this after ${MCP_TOOLS.listFiles.name} to see what's already in the file. No rendered preview or JSX — use ${MCP_TOOLS.getScreenImage.name} or ${MCP_TOOLS.getScreenJsx.name}.

Returns { screens: [{ screenId, name, prompt, fidelity }] }.

- prompt: last generation prompt; empty if the screen was never generated.`;
    },
    shortDescription: "List the screens in a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  getScreenImage: {
    name: "get-screen-image",
    title: "Get Screen Image",
    get description() {
      return `Get a rendered PNG of a screen in one of the current user's Flowstep files. No JSX — use ${MCP_TOOLS.getScreenJsx.name}. Call this once for each screenId returned by ${MCP_TOOLS.aiDesignScreens.name}, ${MCP_TOOLS.aiGenerateScreenVariant.name}, ${MCP_TOOLS.aiEditScreenVariant.name}, ${MCP_TOOLS.addScreenJsx.name}, or ${MCP_TOOLS.replaceScreenJsx.name}, and for ${MCP_TOOLS.importFigma.name} when screenId is set. If ${MCP_TOOLS.importFigma.name} returned null, call ${MCP_TOOLS.listScreens.name} first, then this once per new screenId. Then stop and show the user.

Returns { type: "image", mimeType: "image/png", data }.

- mimeType: always "image/png".
- data: standard base64 of the PNG bytes, not a data URI.`;
    },
    shortDescription: "Get a PNG of a screen in a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  getScreenJsx: {
    name: "get-screen-jsx",
    title: "Get Screen JSX",
    get description() {
      return `Get a screen's JSX and metadata from one of the current user's Flowstep files. No rendered preview — use ${MCP_TOOLS.getScreenImage.name}. Set includeIds when you need element data-id values in jsx to pass in elementIds for ${MCP_TOOLS.aiDesignScreens.name}.

${SCREEN_JSX_DESCRIPTION}

Returns { fileId, screenId, name, prompt, fidelity, screenType, colorTheme, jsx, fragments }.

- jsx: data-id is included only when includeIds is true.
- prompt: last generation prompt; empty if the screen was never generated.
- fragments: shared chrome (e.g., nav, header, logo) stored on the screen. Empty when none. Pass them back on ${MCP_TOOLS.replaceScreenJsx.name} to keep or update them.
- Image URLs in jsx and fragments: keep them exactly as returned. To add, move, or copy an image, follow the image rules in ${MCP_TOOLS.addScreenJsx.name} and ${MCP_TOOLS.replaceScreenJsx.name}.`;
    },
    shortDescription: "Get a screen's JSX and metadata from a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  addScreenJsx: {
    name: "add-screen-jsx",
    title: "Add Screen JSX",
    get description() {
      return `Add a screen's JSX and metadata to one of the current user's Flowstep files. Code-first only — use ${MCP_TOOLS.aiDesignScreens.name} for AI-generated screens or a free-form AI change. Always creates a new screen: use ${MCP_TOOLS.replaceScreenJsx.name} to overwrite an existing one from JSX, or ${MCP_TOOLS.aiEditScreenVariant.name} for a preset AI change. When the new screen is based on an existing one, start from ${MCP_TOOLS.getScreenJsx.name} (without includeIds) for name, fidelity, screenType, colorTheme, and jsx, then change whatever should differ.

${SCREEN_JSX_DESCRIPTION}

Returns { fileId, screenId, warnings? }. warnings lists non-blocking issues (such as an img with no alt); the screen was saved, so fix them with ${MCP_TOOLS.replaceScreenJsx.name}. After adding, call ${MCP_TOOLS.getScreenImage.name} with screenId to view the result.

- jsx: data-id is ignored; the new screen gets new element ids.

${jsxImageRules()}`;
    },
    shortDescription: "Add a screen's JSX and metadata to a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  replaceScreenJsx: {
    name: "replace-screen-jsx",
    title: "Replace Screen JSX",
    get description() {
      return `Replace an existing screen's JSX and metadata in one of the current user's Flowstep files. Code-first only — use ${MCP_TOOLS.aiDesignScreens.name} for a free-form AI change or ${MCP_TOOLS.aiEditScreenVariant.name} for a preset AI change. Use ${MCP_TOOLS.addScreenJsx.name} to create a new screen instead. Start from ${MCP_TOOLS.getScreenJsx.name} (without includeIds) for name, fidelity, screenType, colorTheme, jsx, and fragments, then change whatever should differ. Omit those fields to keep the current values. Pass fragments: [] to clear stored shared chrome.

${SCREEN_JSX_DESCRIPTION}

Returns { fileId, screenId, warnings? }. warnings lists non-blocking issues (such as an img with no alt); the screen was saved, so fix them with another ${MCP_TOOLS.replaceScreenJsx.name} call. After replacing, call ${MCP_TOOLS.getScreenImage.name} with screenId to view the result.

- jsx: data-id is ignored; nested elements get new ids.

${jsxImageRules()}`;
    },
    shortDescription:
      "Replace an existing screen's JSX and metadata in a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true, // overwrites the existing screen in place
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  renameScreen: {
    name: "rename-screen",
    title: "Rename Screen",
    get description() {
      return `Rename a screen's canvas label in one of the current user's Flowstep files. Does not change layout, size, or content — use ${MCP_TOOLS.aiDesignScreens.name} for that.

Returns { screenId, name }.`;
    },
    shortDescription: "Rename a screen in a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  deleteScreen: {
    name: "delete-screen",
    title: "Delete Screen",
    description: `Permanently delete a screen from one of the current user's Flowstep files. Confirm with the user first. name must match the current display name or deletion is aborted.

Returns { deleted: true }.`,
    shortDescription: "Delete a screen from a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: false,
    },
  },

  // File instructions tools
  getFileInstructions: {
    name: "get-file-instructions",
    title: "Get File Instructions",
    description: `Get the instructions attached to one of the current user's Flowstep files: markdown the user wrote, sent as context with every AI request made against the file.

Returns { instructions }.

- instructions: the markdown text, or null if none has been set.`,
    shortDescription: "Get the instructions for a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  updateFileInstructions: {
    name: "update-file-instructions",
    title: "Update File Instructions",
    get description() {
      return `Set or replace the instructions for one of the current user's Flowstep files: markdown sent as context with every AI request made against the file, so product rules, tone and layout conventions need not be repeated in each prompt. Colours, rounding and fonts come from the file's theme; instructions that name them are applied through the theme's tokens. instructions must be a plain string of the raw text — not an object or JSON. If reading from a file, pass that file's text. Use ${MCP_TOOLS.deleteFileInstructions.name} to clear them.

Returns { updated: true }.`;
    },
    shortDescription: "Set or replace the instructions for a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  deleteFileInstructions: {
    name: "delete-file-instructions",
    title: "Delete File Instructions",
    description: `Delete the instructions from one of the current user's Flowstep files. Deleting missing instructions is a no-op.

Returns { deleted: true }.`,
    shortDescription: "Delete the instructions from a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: true, // deleting missing instructions is a no-op
      openWorldHint: false,
    },
  },

  // Chat tools
  getChatHistory: {
    name: "get-chat-history",
    title: "Get Chat History",
    get description() {
      return `Get the chat history for one of the current user's Flowstep files. This is the design conversation associated with the file, not this MCP client's conversation. Use it to resume prior work on the file. Not a substitute for ${MCP_TOOLS.listScreens.name} or ${MCP_TOOLS.getFileInstructions.name}.

Returns { messages }.

- messages: empty when this user has no design conversation associated with the file.`;
    },
    shortDescription: "Get the chat history for a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },

  // Storage tools
  uploadAttachment: {
    name: "upload-attachment",
    title: "Upload Attachment",
    get description() {
      return `Upload a reference file for ${MCP_TOOLS.aiDesignScreens.name}, or an image to place with ${MCP_TOOLS.addScreenJsx.name} or ${MCP_TOOLS.replaceScreenJsx.name}. Bytes never pass through the LLM; this tool does not fetch URLs. Not used by ${MCP_TOOLS.aiGenerateScreenVariant.name} or ${MCP_TOOLS.aiEditScreenVariant.name}.

Use exactly one mode:

A. mimeType + fileName — prepare a one-time PUT to Flowstep. fileName is only a label stored with the upload (the original name with extension, e.g. cat.png, design.md); mimeType sets the stored type. For a remote URL, use the URL basename or a descriptive name with the correct extension. Replace LOCAL_FILE_PATH in uploadCommand with an absolute path and run that curl. Check that it exited 0 before using the result; otherwise nothing was stored, and curl prints the server's error message, so fix what it says and rerun it. Then pass only { id, path, type } into the design tool. For ${MCP_TOOLS.addScreenJsx.name} or ${MCP_TOOLS.replaceScreenJsx.name}, after that curl put assetUrl verbatim in img src or in className as bg-[url(assetUrl)], and only when type is "image". Skipping the curl leaves storage empty. Max ${ATTACHMENT_MAX_SIZE_MB} MB; a larger file fails the curl with HTTP 413 and is not stored.
   Conversation-attached or workspace file: use that path. Remote http(s) URL: download locally first, then Mode A. Never pass the URL as LOCAL_FILE_PATH or as type:image.

B. text + mimeType — ${TEXT_DOCUMENT_MIME_TYPES.join(", ")} only. No curl. Do not use text for images or PDFs. If longer than the text field allows, use Mode A.

Returns { id, path, assetUrl, type, mimeType, uploadUrl?, uploadCommand?, nextStep? }.

- assetUrl: put this URL in the JSX for ${MCP_TOOLS.addScreenJsx.name} or ${MCP_TOOLS.replaceScreenJsx.name}, in img src or bg-[url(...)]. Use it only when type is "image", after the Mode A curl. Do not put path, uploadUrl, or uploadCommand in src.
- type: "image" or "document".
- uploadUrl, uploadCommand, nextStep: set only on Mode A. Do not pass them into design tools.`;
    },
    shortDescription: "Upload a reference image or document.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false, // each call creates a new storage path
      openWorldHint: false,
    },
  },

  // Figma tools
  importFigma: {
    name: "import-figma",
    title: "Import Figma",
    get description() {
      return `Import a Figma frame into one of the current user's Flowstep files as editable canvas elements. The file's organisation must have Figma connected. Each import adds a new screen — importing the same frame twice gives two screens, so delete the old one if you meant to replace it. When screenId is set, call ${MCP_TOOLS.getScreenImage.name} once with it, then stop. When screenId is null, call ${MCP_TOOLS.listScreens.name} and then ${MCP_TOOLS.getScreenImage.name} once per new screenId.

Returns { fileId, screenId, frameCount? }.

- screenId: imported screen, or null when a page URL imported multiple frames.
- frameCount: set only on that page-fallback path.`;
    },
    shortDescription: "Import a Figma frame into a file.",
    pricing: "free",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false, // each call adds another screen
      openWorldHint: true, // fetches the frame from the Figma API
    },
  },

  // Plan & Billing tools
  getPlanDetails: {
    name: "get-plan-details",
    title: "Get Plan Details",
    description: `Get the current user's Flowstep plan, usage, and remaining quota.

Returns { plan: { name, code, type }, subscription: { isPaid, isTrial, startDate, endDate, trialDaysRemaining }, limits, usage, remaining }.

- plan.type: "free" or "paid".
- remaining: quota left per feature (daily/monthly); omitted for unlimited features.`,
    shortDescription: "Get plan, usage, and remaining quota.",
    pricing: "free",
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },

  // AI tools
  aiDesignScreens: {
    name: "ai-design-screens",
    title: "AI Design Screens",
    get description() {
      return `Generate one or more new screens, edit one or more existing ones in place, or both in one turn, from a text description — theming, copy, layout, new flows, or coordinated changes across screens. This is the same design agent as the chat in the Flowstep editor; it decides from message and elementIds what to build or change. Omit fileId to start a new file; pass fileId to work in an existing one. Call ${MCP_TOOLS.listFiles.name} (and ${MCP_TOOLS.listScreens.name}) first when the request could apply to a file the user already has — only omit fileId once none applies or they want to start over. Use ${MCP_TOOLS.aiGenerateScreenVariant.name} for a next-step, error or empty state, or platform variant of one screen, or ${MCP_TOOLS.aiEditScreenVariant.name} for a preset edit or redesign of one screen. Use ${MCP_TOOLS.addScreenJsx.name} or ${MCP_TOOLS.replaceScreenJsx.name} when you already have JSX — this tool always designs from the description and never takes JSX.

elementIds are optional screenIds (${MCP_TOOLS.listScreens.name}) and/or subelement data-ids (${MCP_TOOLS.getScreenJsx.name} with includeIds) to edit in place or use as visual reference for new screens, like a selection in the editor. Say which in message. A screen id targets the whole screen. fileId is required when elementIds is set. File attachments come from ${MCP_TOOLS.uploadAttachment.name} as { id, path, type }. Include webpage URLs in message.

Returns { status, fileId, screenIds, reply? }. After a generated turn, call ${MCP_TOOLS.getScreenImage.name} once per screenId, then stop.

- status: "generated" when screens were built or changed; "chat" when the agent replied without building (reply). screenIds is empty unless status is "generated". The host should ask the user itself if the brief is ambiguous.`;
    },
    shortDescription:
      "Generate new screens, edit existing ones, or both, from a description.",
    pricing: "paid",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true, // may overwrite existing screen content in place
      idempotentHint: false,
      openWorldHint: true, // agent may search Unsplash for photos; Firecrawl for web content
    },
  },
  aiGenerateScreenVariant: {
    name: "ai-generate-screen-variant",
    title: "AI Generate Screen Variant",
    get description() {
      return `Add a screen derived from exactly one existing screen — a next-step, error or empty state, or platform variant of that screen. Not an unrelated new screen and not an in-place change. Use ${MCP_TOOLS.aiDesignScreens.name} with fileId for a screen that is not a variant of an existing one (e.g. add a settings screen) or for a free-form AI change, or ${MCP_TOOLS.aiEditScreenVariant.name} for a preset AI edit or redesign of one screen.

screenId is one screenId from ${MCP_TOOLS.listScreens.name}.

Returns { status, fileId, screenIds }. After a generated turn, call ${MCP_TOOLS.getScreenImage.name} once per screenId, then stop.

- status: always "generated".`;
    },
    shortDescription:
      "Add a next-step, error or empty state, or platform variant of one existing screen.",
    pricing: "paid",
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: true, // agent may search Unsplash for photos; Firecrawl for web content
    },
  },
  aiEditScreenVariant: {
    name: "ai-edit-screen-variant",
    title: "AI Edit Screen Variant",
    get description() {
      return `Modify exactly one existing screen in place using a preset. Theme or polish it, try a different layout or visual style, or redesign it from scratch. Use ${MCP_TOOLS.aiDesignScreens.name} for brand-new screens, a free-form AI change, or a precise subelement edit, and ${MCP_TOOLS.aiGenerateScreenVariant.name} to add a next-step, error or empty state, or platform variant.

screenId is one screenId from ${MCP_TOOLS.listScreens.name}. operationVariant selects either an edit preset (dark_theme, light_theme, make_pretty) or a redesign preset (different_layout, different_style, from_scratch).

Returns { status, fileId, screenIds }. After a generated turn, call ${MCP_TOOLS.getScreenImage.name} once per screenId, then stop.

- status: always "generated".`;
    },
    shortDescription:
      "Apply an edit or redesign preset to one existing screen.",
    pricing: "paid",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true, // overwrites the existing screen in place
      idempotentHint: false,
      openWorldHint: true, // agent may search Unsplash for photos; Firecrawl for web content
    },
  },
} as const;

const joinToolNames = (names: readonly string[]): string => {
  if (names.length <= 1) {
    return names[0] ?? "";
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
};

const paidToolNames = Object.values(MCP_TOOLS)
  .filter((tool) => tool.pricing === "paid")
  .map((tool) => tool.name);

export const MCP_TOOLS_PRICING_INFORMATION = (() => {
  if (paidToolNames.length === 0) {
    return "All tools are free.";
  }
  if (paidToolNames.length === Object.values(MCP_TOOLS).length) {
    return "Using any tool over MCP draws on the same message allowance as the Flowstep app.";
  }
  return `Using ${joinToolNames(paidToolNames)} over MCP draws on the same message allowance as the Flowstep app. All other tools are free.`;
})();
